/**
 * Operations & reliability intents — Paul Telford's feedback (25 Sep 2026),
 * asked through the chat.
 *
 * > Some of the observations might be operator issues and should go to the
 * > Operating Supervisor / Superintendent and Mine control team … whereas
 * > other items should be going to the reliability engineer … or maintenance
 * > planner to schedule parts … the SPC module … FMEA, Control Plans & TARPs
 * > … along with the Driver tree to prioritise which machine to target first.
 *
 * | Intent | Asks | Engine |
 * |---|---|---|
 * | `route_observation` | who should this go to; operator or equipment? | `routeAlert` / `rootCause` / `routingDecisions` |
 * | `operator_training` | which operators need coaching | `trainingNeeds` |
 * | `shift_driver` | who was driving; which shift | `machineOperatorShares` / `shiftLog` |
 * | `target_first` | which machine first; the A$ exposure | `priorities` / `exposureByMachine` |
 * | `spc_status` | is it in control | `spcEngine.imr` (OptiFlow-swappable) |
 * | `tarp_status` | TARP level, one machine or the fleet | `tarpStatuses` / `fleetTarpStatuses` / `TARPS` |
 * | `fmea_top` | highest-RPN failure modes | `fmeaRows` / `fmeaFor` |
 * | `control_plan` | what is measured and the reaction plan | `CONTROL_PLANS` |
 *
 * Every answer reads the shared reliability context through
 * {@link loadReliabilityCtx} (one fetch + attribution per page load, shared
 * with the scenes). The roster, operators and most site-role holders are
 * synthesised for the proof of concept; answers say so where they name people.
 *
 * Cue boundaries with the older families (the gate holds these):
 * - "certificate / expiry / LMS / ticket" is `training` (service); "coaching /
 *   operator / driver / behaviour" is `operator_training`.
 * - "most likely to fail next" stays `risk_ranking`; "target first / exposure /
 *   driver tree" is `target_first`.
 * - "Which technician should take…" stays `dispatch`; "who should this go to"
 *   is `route_observation`.
 *
 * Engine layer — async data loads, otherwise pure.
 *
 * @see docs/engines/reliability.md
 */

import { BY_ID, HEALTH, MINES } from '../../../data/core.ts';
import type { MachineClass, MineCode } from '../../../schema/machine.ts';
import type {
  RootCauseFinding,
  RouteTarget,
  RoutingAction,
  RoutingDecision,
  SiteRole,
  SpcResult,
  TarpStatus,
} from '../../../schema/operations.ts';
import { CLSNAME, fmtk } from '../../oem-engine.ts';
import {
  DRIVER_LABEL,
  exposureByMachine,
  fleetTarpStatuses,
  fmeaFor,
  fmeaRows,
  dailySeries,
  liveSeries,
  machineOperatorShares,
  priorities,
  rootCause,
  rootCauses,
  routeAlert,
  routeFinding,
  routingDecisions,
  shiftLog,
  spcEngine,
  tarpStatuses,
  tarpsFor,
  trainingNeeds,
} from '../../reliability/index.ts';
import { loadReliabilityCtx, type ReliabilityCtx } from '../../reliability/useReliability.ts';
import { ALERTS, type Alert } from '../../seed/alerts.ts';
import { CONTROL_PLANS } from '../../seed/control-plans.ts';
import { metricInfoFor } from '../../seed/metric-info.ts';
import { ROLE_SIDE } from '../../seed/site-roles.ts';
import { TARPS } from '../../seed/tarps.ts';
import { OPERATOR_BY_ID } from '../../seed/operators.ts';
import { classFromText, mineShort, type Entities } from '../entities.ts';
import { MEMORY_NOTE, PAGE_NOTE } from '../page-context.ts';
import { cues, type AnswerCtx, type IntentDef } from './types.ts';

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

/** The intents in this family — the memory reads it to carry a machine over. */
export const OPERATIONS_INTENT_IDS = [
  'route_observation',
  'operator_training',
  'shift_driver',
  'target_first',
  'spc_status',
  'tarp_status',
  'fmea_top',
  'control_plan',
] as const;

/** A question about many things, not one ("which machines…", "across the fleet"). */
const FLEET_SCOPE =
  /\bwhich (machines?|trucks?|loaders?|drills?|rigs?|units?|operators?|drivers?|observations?|alerts?|items?|shifts?|crews?)\b|\bfleet\b|\bacross\b|\bevery\b|\ball (the )?(machines|trucks|sites|mines)\b|\bany (machines?|trucks?)\b/;

/** "this", "it", "that" — the question leans on the page or the last turn. */
const DEICTIC = /\b(this|it|its|that)\b/;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `08 Sep 07:57` from an ISO stamp; `08 Sep` from a date. */
export function stamp(x: string): string {
  const [date, time] = x.split('T');
  const [, m, d] = date.split('-');
  const day = `${d} ${MONTHS[Number(m) - 1] ?? m}`;
  return time ? `${day} ${time.slice(0, 5)}` : day;
}

const pct = (x: number): string => `${Math.round(x * 100)}%`;
const fix = (n: number, dp = 1): string => n.toFixed(dp);

/** `Brodie Keane (stand-in)` — a synthesised person says so. */
const who = (name: string, synthesised: boolean): string => `${name}${synthesised ? ' (stand-in)' : ''}`;

const CAUSE_LABEL: Readonly<Record<RoutingDecision['cause'], string>> = {
  operator: 'operator issue',
  equipment: 'equipment issue',
  mixed: 'both — operator and equipment',
  insufficient: 'not enough signal to call',
};

const LIVE_METRIC_NAME: Readonly<Record<string, string>> = {
  brake_temp_rr_c: 'rear brake temperature',
  intake_air_c: 'intake air temperature',
};

const metricName = (metric: string): string =>
  LIVE_METRIC_NAME[metric] ?? metricInfoFor(metric).name.toLowerCase();

/** The register component on `machine` a question names, if any. */
function componentOn(machine: string, E: Entities): string | undefined {
  const reg = HEALTH.filter((h) => h.machine_id === machine);
  for (const ref of E.components) {
    const hit = reg.find((h) => ref.component.test(h.component)) ?? reg.find((h) => ref.text.test(h.component));
    if (hit) return hit.component;
  }
  return undefined;
}

/** The open alert a component word points at ("the brake alert" → AL-0917). */
function alertForComponent(E: Entities): Alert | undefined {
  for (const ref of E.components) {
    const a = ALERTS.find((x) => ref.component.test(x.comp) || ref.text.test(x.comp));
    if (a) return a;
  }
  return undefined;
}

/** Where a machine came from, as the ported branches print it. */
const fromNote = (from: 'text' | 'page' | 'memory' | 'alert' | undefined): string =>
  from === 'page' ? ` ${PAGE_NOTE}` : from === 'memory' ? ` ${MEMORY_NOTE}` : '';

/** As {@link fromNote}, and a machine picked for its open alert says which. */
const subjectNote = (s: Subject): string =>
  s.from === 'alert' && s.alert ? ` (open alert ${s.alert.id})` : fromNote(s.from);

interface Subject {
  machine?: string;
  from?: 'text' | 'page' | 'memory' | 'alert';
  alert?: Alert;
}

/**
 * The one machine (and alert) an operations question is about — or none, for
 * a fleet question. In order: an alert id or machine in the text; the page,
 * when the question points at it ("this", "it") or is not fleet-scoped; the
 * last turn, when it was an operations answer; the open alert on a named
 * component ("the brake alert").
 */
function subjectOf(
  ctx: Pick<AnswerCtx, 'E' | 'L' | 'page' | 'memory'>,
  opts: { componentAlert?: boolean; memory?: boolean } = {},
): Subject {
  const { E, L, page, memory } = ctx;
  const byId = E.alertIds.map((id) => ALERTS.find((a) => a.id === id)).find(Boolean);
  if (byId) return { machine: byId.machine, from: 'text', alert: byId };
  if (E.machine) {
    const alert = ALERTS.find((a) => a.machine === E.machine);
    const from = E.machineFrom === 'text' || !E.machineFrom ? 'text' : E.machineFrom;
    return { machine: E.machine, from, alert: from === 'page' && page.alert ? ALERTS.find((a) => a.id === page.alert) : alert };
  }
  const fleet = FLEET_SCOPE.test(L) || !!E.mine || !!E.cls;
  if (!fleet && page.machine) {
    const alert = page.alert ? ALERTS.find((a) => a.id === page.alert) : ALERTS.find((a) => a.machine === page.machine);
    return { machine: page.machine, from: 'page', alert };
  }
  const mem = memory as { topic?: string; machine?: string; alert?: string } | undefined;
  if (
    opts.memory !== false &&
    !fleet &&
    mem?.machine &&
    BY_ID[mem.machine] &&
    (mem.topic === 'operations' || DEICTIC.test(L)) &&
    !E.components.length
  ) {
    const alert = mem.alert ? ALERTS.find((a) => a.id === mem.alert) : ALERTS.find((a) => a.machine === mem.machine);
    return { machine: mem.machine, from: 'memory', alert };
  }
  if (opts.componentAlert !== false && !fleet) {
    const a = alertForComponent(E);
    if (a) return { machine: a.machine, from: 'alert', alert: a };
  }
  return {};
}

/** The mine and class a fleet question is scoped to. */
function scopeOf(E: Entities): { mine?: MineCode; cls?: readonly MachineClass[]; label: string } {
  const mine = E.mine;
  const cls = E.cls?.classes;
  const label = [mine ? MINES[mine].name : null, E.cls?.label ?? null].filter(Boolean).join(' · ');
  return { mine, cls, label: label ? ` · ${label}` : '' };
}

const inScope = (machine: string, s: { mine?: MineCode; cls?: readonly MachineClass[] }): boolean =>
  !!BY_ID[machine] && (!s.mine || BY_ID[machine].mine === s.mine) && (!s.cls || s.cls.includes(BY_ID[machine].cls));

/** Load the shared reliability context, recording it in the trace. */
async function reliability(kit: AnswerCtx['kit']): Promise<ReliabilityCtx> {
  const ctx = await loadReliabilityCtx();
  kit.ran('reliability_ctx');
  return ctx;
}

const machineTab = (machine: string, tab: 'operators' | 'reliability', view?: string): string =>
  `/machines/${machine}?tab=${tab}${view ? `&view=${view}` : ''}`;

const SOURCES_ATTRIBUTION = ['shift_daily', 'events_daily', 'operator roster (synthesised)'];

// ---------------------------------------------------------------------------
// route_observation — operator or equipment, and who it goes to
// ---------------------------------------------------------------------------

const ROLE_WORDS: readonly (readonly [RegExp, SiteRole])[] = [
  [/operating supervisor|\bsupervisors?\b/, 'Operating Supervisor'],
  [/superintendents?/, 'Superintendent'],
  [/mine control/, 'Mine Control'],
  [/reliability engineers?/, 'Reliability Engineer'],
  [/maintenance planners?|\bplanners?\b/, 'Maintenance Planner'],
];

function routeLine(r: RouteTarget, actions: readonly RoutingAction[]): string {
  const mine = actions.filter((a) => a.owner === r.role);
  // The planner's "why" is the reservation again; the action says it with the stock.
  const why = mine.some((a) => a.kind === 'schedule-parts') ? '' : ` ${r.why}`;
  return `- **${r.role}** — ${who(r.personName, r.synthesised)} · ${r.channel}.${why}${mine.length ? ` ${mine.map((a) => a.text).join(' ')}` : ''}`;
}

/** The two-sided route list — Operations, then Maintenance. */
function routeBlock(d: RoutingDecision): string {
  const ops = d.routes.filter((r) => ROLE_SIDE[r.role] === 'operations');
  const mx = d.routes.filter((r) => ROLE_SIDE[r.role] === 'maintenance');
  const out: string[] = [];
  if (ops.length) out.push(`**Operations**\n${ops.map((r) => routeLine(r, d.actions)).join('\n')}`);
  else out.push(`**Operations** — nothing to send: the pattern is not behaviour.`);
  if (mx.length) out.push(`**Maintenance**\n${mx.map((r) => routeLine(r, d.actions)).join('\n')}`);
  else out.push(`**Maintenance** — nothing to schedule: the component is in good condition.`);
  return out.join('\n\n');
}

/** The finding worth routing on a machine with no alert and no component named. */
function worstFinding(machine: string, ctx: ReliabilityCtx): RootCauseFinding {
  const rank: Record<RootCauseFinding['cause'], number> = { mixed: 3, operator: 2, equipment: 1, insufficient: 0 };
  const flagged = rootCauses(ctx)
    .filter((f) => f.machine === machine)
    .sort((a, b) => rank[b.cause] - rank[a.cause] || b.confidence - a.confidence)[0];
  if (flagged) return flagged;
  const worst = HEALTH.filter((h) => h.machine_id === machine).sort((a, b) => a.health_score - b.health_score)[0];
  return rootCause(machine, worst.component, ctx);
}

export const routeObservation: IntentDef = {
  id: 'route_observation',
  family: 'operations',
  priority: 97,
  examples: [
    'Who should the T-09 brake alert go to?',
    'Is T-09 a driver issue or worn brakes?',
    'Which observations go to Mine Control?',
  ],
  score: cues({
    any: [
      /who should .{0,40}\bgo to\b|who (does|should|would) (this|it|that) go to|\bgo to\?/,
      /who (gets|owns|handles|should (get|own|handle|see|action)) (this|it|that|the .{0,30}(alert|observation|finding))/,
      /\broute\b|\brouting\b|\bpush (it|this|that)\b/,
      /operator or (an? )?(equipment|machine|maintenance)|equipment or (an? )?operator|(operator|driver)s? (issue|problem|error|fault|behaviou?r)s? or\b|\bor (the )?(an? )?(equipment|machine) (issue|problem|fault)|is it the (driver|operator)|(driver|operator) or (the )?(worn|machine|equipment|brakes?)/,
      /whose (problem|issue|fault)/,
      /\bobservations?\b/,
      /mine control|superintendent|operating supervisor|reliability engineer|maintenance planner/,
    ],
    not: [/certificat|\blms\b|expir|which technician/],
    base: 7,
  }),
  async answer(actx) {
    const { kit, E, L } = actx;
    const ctx = await reliability(kit);
    const subj = subjectOf(actx);

    if (subj.machine) {
      const machine = subj.machine;
      const named = componentOn(machine, E);
      let d: RoutingDecision;
      if (subj.alert && (!named || named === subj.alert.comp)) {
        d = routeAlert(subj.alert, ctx);
        kit.ran('route_alert', { alert: subj.alert.id });
      } else {
        const f = named ? rootCause(machine, named, ctx) : worstFinding(machine, ctx);
        kit.ran('root_cause', { machine_id: machine, component: f.component });
        d = routeFinding(f, ctx);
      }
      kit.focus({ machine, mine: d.mine, component: d.component, ...(d.alertId ? { alert: d.alertId } : {}) });
      kit.remember({ cause: d.cause });
      const verdict =
        d.cause === 'mixed'
          ? 'It goes to **both sides** — operations for the driving, maintenance for the wear.'
          : d.cause === 'operator'
            ? 'It goes to **operations** — the Operating Supervisor, Superintendent and Mine Control; maintenance has nothing to change.'
            : d.cause === 'equipment'
              ? 'It goes to **maintenance** — the Reliability Engineer and the Maintenance Planner; the events follow the hours, not a driver.'
              : 'Neither signal is strong enough to call, so it goes to the **Reliability Engineer** to triage before anyone acts.';
      const markdown =
        `**${machine}${fromNote(subj.from)} · ${d.component}**${d.alertId ? ` (${d.alertId})` : ''} — ` +
        `**${CAUSE_LABEL[d.cause]}**, ${pct(d.confidence)} confidence. ${verdict}\n\n` +
        `Evidence:\n${d.evidence.slice(0, 4).map((e) => `- ${e}`).join('\n')}\n\n` +
        `${routeBlock(d)}\n\n` +
        `The operator split comes from the shift roster, which is synthesised for this proof of concept; the alarm counts, hours and condition scores are the data pack's.`;
      return kit.done(markdown, [...SOURCES_ATTRIBUTION, 'component_health', 'site roles'], {
        navigate: d.alertId ? `/issues/alerts/${d.alertId}` : machineTab(machine, 'operators'),
      });
    }

    // Fleet: every routed observation, filtered by role, cause and scope.
    const all = routingDecisions(ctx);
    kit.ran('routing_decisions', { count: all.length });
    const s = scopeOf(E);
    const role = ROLE_WORDS.find(([re]) => re.test(L))?.[1];
    const cause: RoutingDecision['cause'] | undefined = /\boperator|driver|behaviou?r/.test(L)
      ? 'operator'
      : /equipment|maintenance issue|wear/.test(L)
        ? 'equipment'
        : undefined;
    const rows = all
      .filter((d) => inScope(d.machine, s))
      .filter((d) => !role || d.routes.some((r) => r.role === role))
      .filter((d) => !cause || d.cause === cause || d.cause === 'mixed');
    const count = (c: RoutingDecision['cause']) => rows.filter((d) => d.cause === c).length;
    const head = role
      ? `**${rows.length} observations go to ${role}**${s.label}`
      : `**${rows.length} routed observations**${s.label}`;
    const table =
      `| Observation | Call | Goes to |\n|---|---|---|\n` +
      rows
        .slice(0, 8)
        .map((d) => {
          const to = role
            ? d.routes.filter((r) => r.role === role).map((r) => `${who(r.personName, r.synthesised)} (${r.channel})`).join('; ')
            : [...new Set(d.routes.map((r) => ROLE_SIDE[r.role]))].map((x) => (x === 'operations' ? 'Operations' : 'Maintenance')).join(' + ');
          return `| ${d.alertId ? `${d.alertId} · ` : ''}${d.machine} ${d.component} | ${d.cause} ${pct(d.confidence)} | ${to} |`;
        })
        .join('\n');
    if (rows[0]) kit.focus({ machine: rows[0].machine, mine: rows[0].mine, component: rows[0].component });
    const markdown =
      `${head} — ${count('operator')} operator, ${count('equipment')} equipment, ${count('mixed')} both` +
      `${count('insufficient') ? `, ${count('insufficient')} to triage` : ''}. Open alerts first, then flagged findings.\n\n` +
      (rows.length ? `${table}${rows.length > 8 ? `\n\n…and ${rows.length - 8} more on the routing board.` : ''}\n\n` : '') +
      `Operator issues go to the Operating Supervisor, Superintendent and Mine Control; equipment issues to the Reliability Engineer and Maintenance Planner; "both" goes to all five. ` +
      `Ask about one — "who should the T-09 brake alert go to?" — for the evidence and the people.`;
    return kit.done(markdown, [...SOURCES_ATTRIBUTION, 'component_health', 'site roles'], { navigate: '/issues/routing' });
  },
};

// ---------------------------------------------------------------------------
// operator_training — who needs coaching
// ---------------------------------------------------------------------------

export const operatorTraining: IntentDef = {
  id: 'operator_training',
  family: 'operations',
  priority: 96,
  examples: ['Which operators need training?', 'Who needs coaching at Mount Isolde?'],
  score: cues({
    any: [
      /\bcoach(ing|ed|es)?\b|\bretrain|\brefresher\b/,
      /(operators?|drivers?)\b.{0,40}\b(training|train|trained)\b/,
      /\b(training|train)\b.{0,30}\b(operators?|drivers?)\b/,
      /(operator|driver|driving) (behaviou?r|technique|habits?)|bad driving|machine abuse|\babuse\b/,
      /which (operators?|drivers?)\b/,
    ],
    not: [/certificat|\blms\b|expir|\btickets?\b|which technician/],
    penalty: [[/who was (driving|operating)|who drove|which shift|night shift|day shift/, 3]],
    base: 7,
  }),
  async answer({ kit, E }) {
    const ctx = await reliability(kit);
    const needs = trainingNeeds(ctx.attribution);
    kit.ran('training_needs', { flagged: needs.length });
    const s = scopeOf(E);
    const rows = needs.filter((n) => (!s.mine || n.mine === s.mine) && (!s.cls || s.cls.includes(n.class)));
    const siteName = s.mine ? MINES[s.mine].name : 'the fleet';
    if (!rows.length) {
      const markdown =
        `**No operator stands out at ${siteName}**${E.cls ? ` on ${E.cls.label}` : ''} — nobody runs at 1.8× their site peers ` +
        `with enough hours (≥ 20 h) and events (≥ 5) for it to be more than chance (Poisson p ≤ 0.05). ` +
        `Across the fleet ${needs.length} operator${needs.length === 1 ? '' : 's'} ${needs.length === 1 ? 'does' : 'do'}.`;
      if (s.mine) kit.focus({ mine: s.mine });
      return kit.done(markdown, SOURCES_ATTRIBUTION, { navigate: '/issues/routing?tab=training' });
    }
    const table =
      `| Operator | Site · crew | Class | Rate vs peers | Recommendation |\n|---|---|---|---|---|\n` +
      rows
        .slice(0, 6)
        .map(
          (n) =>
            `| ${who(n.name, n.synthesised)} | ${mineShort(n.mine)} · ${n.crew} | ${CLSNAME[n.class]} | ` +
            `${fix(n.ratePer100h)} vs ${fix(n.peerRate)} /100 h (**${fix(n.ratio)}×**) | ${n.recommendation.split(' — ')[0]} |`,
        )
        .join('\n');
    const top = rows[0];
    kit.focus({ mine: top.mine });
    kit.remember({ operator: top.name });
    const markdown =
      `**${rows.length} operator${rows.length === 1 ? '' : 's'} need${rows.length === 1 ? 's' : ''} coaching${s.mine ? ` at ${MINES[s.mine].name}` : ''}**` +
      `${E.cls ? ` · ${E.cls.label}` : ''} — each runs at 1.8× or more of their site peers' rate of operator-sensitive events and abuse, ` +
      `on at least 20 hours, and the gap is unlikely to be chance (Poisson p ≤ 0.05).\n\n${table}` +
      `${rows.length > 6 ? `\n\n…and ${rows.length - 6} more on the training list.` : ''}\n\n` +
      `**${top.name}** leads: ${top.events} events in ${fix(top.hours, 0)} h (${top.topCodes.slice(0, 2).map((c) => `${c.code} × ${c.count}`).join(', ')}). ` +
      `Recommended: ${top.recommendation.replace(/\.$/, '')}. Coaching goes through the site's Operating Supervisor. The roster and most operator names are synthesised for this proof of concept; the event counts are the data pack's.`;
    return kit.done(markdown, SOURCES_ATTRIBUTION, { navigate: '/issues/routing?tab=training' });
  },
};

// ---------------------------------------------------------------------------
// shift_driver — who was driving, which shift
// ---------------------------------------------------------------------------

export const shiftDriver: IntentDef = {
  id: 'shift_driver',
  family: 'operations',
  priority: 95,
  examples: ['Who was driving T-09 on night shift?', 'Which shift has the most brake exceedances?'],
  score: cues({
    any: [
      /who was (driving|operating|at the wheel|on (it|the|shift))|who drove|who('s| is) (been )?driving|who was driving/,
      /which shift|night shift|day shift|nightshift|dayshift|\bper shift\b|shift log|by shift/,
      /which (crew|operator)\b.{0,40}\b(exceed|events?|alarms?|most)/,
    ],
    not: [/\bcost|spend|expensive|\bprice/],
    base: 7,
  }),
  async answer(actx) {
    const { kit, E, L } = actx;
    const ctx = await reliability(kit);
    const att = ctx.attribution;
    const onlyShift = /night/.test(L) ? 'N' : /\bday\b|day shift|dayshift/.test(L) ? 'D' : null;
    const subj = subjectOf(actx, { componentAlert: !/which shift|which crew|which operator/.test(L) });

    if (subj.machine) {
      const machine = subj.machine;
      const component = componentOn(machine, E) ?? subj.alert?.comp;
      const shares = machineOperatorShares(att, machine, component ? { component } : {});
      kit.ran('machine_operator_shares', { machine_id: machine, ...(component ? { component } : {}) });
      const log = shiftLog(att, machine);
      kit.ran('shift_log', { machine_id: machine });
      const shifts = onlyShift ? log.filter((e) => e.shift === onlyShift) : log;
      const counted = (e: (typeof att.events)[number]) =>
        e.machine === machine && e.sensitivity !== 'none' && e.component !== null && (!component || e.component === component);
      const byOp = new Map<string, { name: string; shifts: number; hours: number; events: number }>();
      for (const e of shifts) {
        const o = byOp.get(e.operatorId) ?? { name: e.operatorName, shifts: 0, hours: 0, events: 0 };
        o.shifts += 1;
        o.hours += e.hours;
        byOp.set(e.operatorId, o);
      }
      for (const e of att.events) {
        if (!counted(e) || (onlyShift && e.shift !== onlyShift)) continue;
        const o = byOp.get(e.operatorId);
        if (o) o.events += e.count;
      }
      const regulars = [...byOp]
        .map(([id, o]) => ({ ...o, name: who(o.name, OPERATOR_BY_ID[id]?.synthesised ?? true) }))
        .sort((a, b) => b.events - a.events || b.shifts - a.shifts)
        .slice(0, 3);
      const totalEvents = shares.reduce((a, s) => a + s.events, 0);
      const worst = shares.find((s) => s.events > 0);
      const shiftEvents = (code: 'D' | 'N') =>
        att.events.filter((e) => counted(e) && e.shift === code).reduce((a, e) => a + e.count, 0);
      const d = shiftEvents('D');
      const n = shiftEvents('N');
      const last = shifts[shifts.length - 1];
      kit.focus({ machine, mine: BY_ID[machine].mine, ...(component ? { component } : {}), ...(subj.alert ? { alert: subj.alert.id } : {}) });
      const shiftWord = onlyShift === 'N' ? 'night shift' : onlyShift === 'D' ? 'day shift' : 'both shifts';
      const markdown =
        `**${machine}${fromNote(subj.from)} · who was driving${component ? ` — ${component}` : ''}** (${shiftWord}, ${stamp(log[0].date)}–${stamp(log[log.length - 1].date)})\n\n` +
        `| Operator | Shifts | Hours | Events |\n|---|---|---|---|\n` +
        regulars.map((o) => `| ${o.name} | ${o.shifts} | ${fix(o.hours)} | ${o.events} |`).join('\n') +
        `\n\n` +
        (worst && totalEvents
          ? `**${who(worst.name, worst.synthesised)}** (crew ${worst.crew}) carries **${worst.events} of ${totalEvents}** operator-sensitive events (${pct(worst.eventShare)}) on ${pct(worst.hoursShare)} of the hours. `
          : `No operator-sensitive events on ${machine}${component ? `'s ${component.toLowerCase()}` : ''} in the window. `) +
        `Day shift logged ${d}, night shift ${n}.` +
        (last ? ` Last ${onlyShift === 'N' ? 'night ' : onlyShift === 'D' ? 'day ' : ''}shift on record: ${stamp(last.date)} ${last.shift === 'N' ? 'night' : 'day'}, ${last.operatorName} (crew ${last.crew}), ${fix(last.hours)} h.` : '') +
        `\n\nThe shift split and operators are synthesised for this proof of concept (the data pack has machine-day hours and alarm counts, no shift or driver).`;
      return kit.done(markdown, SOURCES_ATTRIBUTION, { navigate: machineTab(machine, 'operators') });
    }

    // Fleet: which shift carries the events.
    const s = scopeOf(E);
    const ref = E.components[0];
    const evs = att.events.filter(
      (e) =>
        e.sensitivity !== 'none' &&
        e.component !== null &&
        inScope(e.machine, s) &&
        (!ref || ref.component.test(e.component) || ref.text.test(e.component)),
    );
    kit.ran('shift_split', { ...(ref ? { component: ref.word } : {}), ...(s.mine ? { mine: s.mine } : {}) });
    const sum = (code: 'D' | 'N') => evs.filter((e) => e.shift === code).reduce((a, e) => a + e.count, 0);
    const d = sum('D');
    const n = sum('N');
    const perMachine = new Map<string, { D: number; N: number }>();
    for (const e of evs) {
      const x = perMachine.get(e.machine) ?? { D: 0, N: 0 };
      x[e.shift] += e.count;
      perMachine.set(e.machine, x);
    }
    const skewed = [...perMachine]
      .filter(([, x]) => x.D + x.N >= 5)
      .map(([m, x]) => ({ m, total: x.D + x.N, nShare: x.N / (x.D + x.N) }))
      .sort((a, b) => Math.abs(b.nShare - 0.5) * b.total - Math.abs(a.nShare - 0.5) * a.total)
      .slice(0, 3);
    const lead = n > d ? 'Night' : d > n ? 'Day' : 'Neither';
    const what = ref ? `${ref.word} exceedances` : 'operator-sensitive events';
    const top = skewed[0];
    let topLine = '';
    if (top) {
      const comp = componentOn(top.m, E);
      const shares = machineOperatorShares(att, top.m, comp ? { component: comp } : {});
      const w = shares.find((x) => x.events > 0);
      topLine = ` The most lopsided machine is **${top.m}** (${mineShort(BY_ID[top.m].mine)}): ${pct(top.nShare)} of its ${top.total} on nights` +
        `${w ? ` — ${who(w.name, w.synthesised)} (crew ${w.crew}) carries ${pct(w.eventShare)} of its events on ${pct(w.hoursShare)} of the hours` : ''}.`;
      kit.focus({ machine: top.m, mine: BY_ID[top.m].mine });
    }
    const markdown =
      `**${lead} shift ${lead === 'Neither' ? 'leads' : 'has the most'} ${what}**${s.label} — day ${d}, night ${n}` +
      `${d + n ? ` (${pct(n / (d + n))} on nights)` : ''}.${topLine}\n\n` +
      (skewed.length
        ? `| Machine | Events | On nights |\n|---|---|---|\n${skewed.map((x) => `| ${x.m} | ${x.total} | ${pct(x.nShare)} |`).join('\n')}\n\n`
        : '') +
      `Events land on the shift whose hours they fell in; the D/N split and the operators are synthesised for this proof of concept.`;
    return kit.done(markdown, SOURCES_ATTRIBUTION, top ? { navigate: machineTab(top.m, 'operators') } : undefined);
  },
};

// ---------------------------------------------------------------------------
// target_first — the driver tree's priorities
// ---------------------------------------------------------------------------

export const targetFirst: IntentDef = {
  id: 'target_first',
  family: 'operations',
  priority: 94,
  examples: ['Which machine should we target first?', "What's our biggest exposure?", 'Show me the driver tree'],
  score: cues({
    any: [
      /target(ed)? first|tackle first|(fix|attack|address|go after|start with|focus on|work on) first|first to (target|fix|tackle)/,
      /which machines? (should|do|would) (we|i) (target|tackle|prioriti[sz]e|attack)/,
      /\bexposure\b/,
      /driver tree|driver-tree/,
      /\bpriorit(y|ies|ise|ize|ised|ized)\b/,
      /who owns the top|top (three|3|five|5)\b/,
    ],
    not: [/\b(report|e-?mail|deck|presentation)\b/, /\bpm\b|work orders?/],
    base: 7,
  }),
  async answer({ kit, E, L }) {
    const ctx = await reliability(kit);
    const s = scopeOf(E);
    const all = priorities(ctx, 100);
    kit.ran('driver_tree_priorities', { ...(s.mine ? { mine: s.mine } : {}) });
    const exposure = exposureByMachine(ctx).filter((x) => inScope(x.machine, s));
    const total = exposure.reduce((a, x) => a + x.valueAud, 0);
    const rows = all.filter((p) => inScope(p.machine, s));
    const n = /\b(three|3)\b/.test(L) ? 3 : 5;
    const top = rows.slice(0, n);
    if (!top.length) {
      const markdown = `**No exposure${s.label}** — no open alert and no machine above the 0.35 risk line.`;
      return kit.done(markdown, ['driver tree', 'predict_risk', 'rate card'], { navigate: '/priorities' });
    }
    const mines = new Set(exposure.map((x) => x.mine)).size;
    const table =
      `| # | Machine | Exposure | Top driver | Owner |\n|---|---|---|---|---|\n` +
      top
        .map(
          (p, i) =>
            `| ${i + 1} | **${p.machine}** ${p.component}${p.alertId ? ` (${p.alertId})` : ''} | ${fmtk(p.valueAud)} | ` +
            `${p.topDriverLabel} — ${p.cause} | ${p.owner ? `${who(p.owner.personName, p.owner.synthesised)}, ${p.owner.role}` : '—'} |`,
        )
        .join('\n');
    const first = top[0];
    kit.focus({ machine: first.machine, mine: first.mine, component: first.component, ...(first.alertId ? { alert: first.alertId } : {}) });
    kit.remember({ ranked: top.map((p) => p.machine) });
    const topShare = total ? top.reduce((a, p) => a + p.valueAud, 0) / total : 0;
    const mix = new Map<string, number>();
    for (const x of exposure) for (const d of x.drivers) mix.set(DRIVER_LABEL[d.kind], (mix.get(DRIVER_LABEL[d.kind]) ?? 0) + d.valueAud);
    const mixText = [...mix]
      .sort((a, b) => b[1] - a[1])
      .filter(([, v]) => v >= 500)
      .map(([k, v]) => `${k} ${fmtk(v)} (${pct(total ? v / total : 0)})`)
      .join(', ');
    const markdown =
      `**Target first${s.label}** — ${fmtk(total)} of exposure across ${exposure.length} machine${exposure.length === 1 ? '' : 's'}` +
      `${s.mine ? '' : ` at ${mines} mine${mines === 1 ? '' : 's'}`}; the top ${top.length} carry ${pct(topShare)} of it.\n\n${table}\n\n` +
      `**What drives it:** ${mixText}.\n\n` +
      `Start with **${first.machine}**: ${first.topDriverText}. ` +
      `Exposure is the cost of doing nothing on an open alert (planned job against the failure), or risk × the class's unplanned cost on a flagged machine; ` +
      `each machine's A$ is split across its drivers — condition, drift, operator behaviour, late PMs and parts lead time — and the owner is the routed person whose role owns the biggest one.`;
    return kit.done(markdown, ['driver tree', 'predict_risk', 'cost of inaction', 'work_orders.csv'], { navigate: '/priorities' });
  },
};

// ---------------------------------------------------------------------------
// spc_status — I-MR chart
// ---------------------------------------------------------------------------

const STATE_LABEL: Readonly<Record<SpcResult['state'], string>> = {
  'in-control': 'in control',
  'out-of-control': 'out of control',
  insufficient: 'not enough points to chart',
};

function spcFor(machine: string, ctx: ReliabilityCtx): { r: SpcResult; basis: 'live' | 'daily' } | null {
  const live = liveSeries(machine, ctx.live);
  const series = live ?? dailySeries(machine, ctx.dailyMetric);
  if (!series || !series.points.length) return null;
  return { r: spcEngine.imr(series), basis: live ? 'live' : 'daily' };
}

function spcLines(machine: string, r: SpcResult, basis: 'live' | 'daily'): string {
  const u = r.unit;
  const name = metricName(r.metric);
  const span = `${stamp(r.points[0].x)} – ${stamp(r.points[r.points.length - 1].x)}`;
  const src = basis === 'live' ? `Live replay, ${r.points.length} samples, ${span}` : `Shift-end daily mean, ${r.points.length} days, ${span}`;
  if (r.state === 'insufficient') {
    return (
      `**${machine} · ${name}** — **${STATE_LABEL[r.state]}** (${src.charAt(0).toLowerCase()}${src.slice(1)}). An I-MR chart needs at least 20 points to set control limits; ` +
      `with ${r.points.length} I won't call it in or out of control. What there is: mean ${fix(r.mean)} ${u}` +
      `${r.usl !== undefined ? ` against the ${r.usl} ${u} limit${r.cpk !== undefined ? ` (provisional Cpk ${r.cpk.toFixed(2)})` : ''}` : ''}. ` +
      `${basis === 'daily' ? `${machine} reports shift-end means only — a live feed, or a longer history, would chart.` : ''}`
    ).trim();
  }
  const rules = new Map<number, number>();
  for (const sg of r.signals) rules.set(sg.rule, (rules.get(sg.rule) ?? 0) + 1);
  const ruleText = [...rules].sort((a, b) => a[0] - b[0]).map(([k, c]) => `rule ${k} × ${c}`).join(', ');
  const cpkText =
    r.cpk === undefined
      ? 'no spec limit'
      : `Cpk **${r.cpk.toFixed(2)}** vs ${r.usl ?? r.lsl} ${u} (${r.cpk < 1 ? 'not capable' : r.cpk < 1.33 ? 'below the 1.33 target' : 'capable'})`;
  const byPoint = new Map<number, string[]>();
  for (const sg of r.signals) byPoint.set(sg.index, [...(byPoint.get(sg.index) ?? []), `rule ${sg.rule}: ${sg.description}`]);
  const points = [...byPoint];
  const signals = points.slice(0, 4).map(([i, text]) => `- ${stamp(r.points[i].x)} · ${r.points[i].value} ${u} — ${text.join('; ')}`);
  return (
    `**${machine} · ${name}** — **${STATE_LABEL[r.state]}**${r.signals.length ? ` (${r.signals.length} signal${r.signals.length === 1 ? '' : 's'}: ${ruleText})` : ''}. ` +
    `${src}.\n\n` +
    `Mean ${fix(r.mean)} ${u}, σ ${fix(r.sigma)}, UCL ${fix(r.ucl)} / LCL ${fix(r.lcl)} (limits from the first ${r.baselineN} points); ${cpkText}.` +
    (signals.length ? `\n\n${signals.join('\n')}${points.length > 4 ? `\n- …and ${points.length - 4} more` : ''}` : '')
  );
}

export const spcStatus: IntentDef = {
  id: 'spc_status',
  family: 'operations',
  priority: 93,
  examples: ['Is T-09 in control?', 'SPC for T-09', 'Control chart for brake temperature'],
  score: cues({
    any: [/\bspc\b|\bin control\b|out of control|control charts?|statistical process|\bi-?mr\b|\bcpk\b|process capab|run rules?/],
    not: [/control plans?|mine control/],
    base: 7,
  }),
  async answer(actx) {
    const { kit } = actx;
    const ctx = await reliability(kit);
    const subj = subjectOf(actx);
    const tail =
      `\n\nEngine: ${spcEngine.label}. It sits behind one \`SpcEngine\` seam, so Miles's OptiFlow SPC module replaces it without changing this answer or the chart.`;
    if (subj.machine) {
      const machine = subj.machine;
      const hit = spcFor(machine, ctx);
      kit.ran('spc_imr', { machine_id: machine, engine: spcEngine.id });
      kit.focus({ machine, mine: BY_ID[machine].mine, ...(subj.alert ? { alert: subj.alert.id, component: subj.alert.comp } : {}) });
      if (!hit) {
        const markdown = `**${machine}${subjectNote(subj)}** has no tracked metric to chart — no live replay and no daily metric in the data pack.`;
        return kit.done(markdown, ['daily telemetry'], { navigate: machineTab(machine, 'reliability', 'spc') });
      }
      kit.remember({ spc: hit.r.state });
      const markdown = spcLines(`${machine}${subjectNote(subj)}`, hit.r, hit.basis) + tail;
      return kit.done(markdown, [hit.basis === 'live' ? 'telemetry-live' : 'daily telemetry', 'SPC engine'], {
        navigate: machineTab(machine, 'reliability', 'spc'),
      });
    }
    // Fleet: every machine with a series, out of control first.
    const s = scopeOf(actx.E);
    const results = Object.keys(BY_ID)
      .filter((m) => inScope(m, s))
      .map((m) => ({ m, hit: spcFor(m, ctx) }))
      .filter((x): x is { m: string; hit: NonNullable<ReturnType<typeof spcFor>> } => !!x.hit);
    kit.ran('spc_imr', { machines: results.length, engine: spcEngine.id });
    const out = results.filter((x) => x.hit.r.state === 'out-of-control');
    const inC = results.filter((x) => x.hit.r.state === 'in-control');
    const short = results.filter((x) => x.hit.r.state === 'insufficient');
    if (out[0]) kit.focus({ machine: out[0].m, mine: BY_ID[out[0].m].mine });
    const markdown =
      `**SPC across the fleet${s.label}** — ${out.length} out of control, ${inC.length} in control, ${short.length} with too few points to chart.\n\n` +
      (out.length ? `${out.map((x) => spcLines(x.m, x.hit.r, x.hit.basis)).join('\n\n')}\n\n` : '') +
      (inC.length ? `In control: ${inC.map((x) => x.m).join(', ')}.\n\n` : '') +
      `Only the live replays carry the 20+ points an I-MR chart needs; the ${short.length} shift-end machines report 11 daily means, so I don't call them either way.` +
      tail;
    return kit.done(markdown, ['telemetry-live', 'daily telemetry', 'SPC engine'], out[0] ? { navigate: machineTab(out[0].m, 'reliability', 'spc') } : undefined);
  },
};

// ---------------------------------------------------------------------------
// tarp_status — the Trigger Action Response Plan level
// ---------------------------------------------------------------------------

const BASIS_LABEL: Readonly<Record<TarpStatus['basis'], string>> = {
  live: 'live CAN feed',
  daily: 'shift-end daily mean',
  events: 'alarm-event counts',
};

const LEVEL_RANK: Readonly<Record<TarpStatus['level'], number>> = { Normal: 0, 'Level 1': 1, 'Level 2': 2, 'Level 3': 3 };

/** The TARPs a question names by component or metric words. */
function tarpsNamed(E: Entities, L: string): typeof TARPS {
  const byComp = TARPS.filter((t) =>
    E.components.some((ref) => ref.component.test(t.component) || ref.text.test(t.component) || ref.text.test(t.title)),
  );
  const pool = byComp.length ? byComp : TARPS.filter((t) => L.includes(t.metric.replace(/_/g, ' ')) || L.includes(t.title.toLowerCase().split(' — ')[0]));
  const temp = /temperature|\btemp\b|hot\b|heat/.test(L);
  const narrowed = temp ? pool.filter((t) => /temp/i.test(t.metric) || /temperature/i.test(t.title)) : pool;
  return narrowed.length ? narrowed : pool;
}

export const tarpStatus: IntentDef = {
  id: 'tarp_status',
  family: 'operations',
  priority: 92,
  examples: ["What's the TARP for brake temperature?", 'What TARP level is T-09 at?', 'Which machines are above normal TARP?'],
  score: cues({
    any: [/\btarps?\b|trigger[- ]action|trigger action response/],
    base: 7,
  }),
  async answer(actx) {
    const { kit, E, L } = actx;
    const ctx = await reliability(kit);
    const fleetAsk = FLEET_SCOPE.test(L) || /above normal|not normal|escalated/.test(L);
    const named = fleetAsk ? [] : tarpsNamed(E, L);
    const subj: Subject = fleetAsk ? {} : subjectOf(actx, { componentAlert: false });

    if (subj.machine) {
      const machine = subj.machine;
      const defs = new Map(tarpsFor(machine).map((t) => [t.id, t]));
      const all = tarpStatuses(machine, ctx)
        .slice()
        .sort((a, b) => LEVEL_RANK[b.level] - LEVEL_RANK[a.level] || a.tarpId.localeCompare(b.tarpId));
      kit.ran('tarp_statuses', { machine_id: machine });
      const want = named.length ? all.filter((x) => named.some((t) => t.id === x.tarpId)) : all;
      const list = want.length ? want : all;
      kit.focus({ machine, mine: BY_ID[machine].mine, ...(subj.alert ? { alert: subj.alert.id } : {}) });
      if (!list.length) {
        const markdown = `**${machine}${subjectNote(subj)}** has no TARP covering it — none is written for the ${CLSNAME[BY_ID[machine].cls].toLowerCase()} class yet.`;
        return kit.done(markdown, ['TARPs'], { navigate: machineTab(machine, 'reliability', 'tarp') });
      }
      const head = list[0];
      const def = defs.get(head.tarpId);
      if (def) kit.focus({ component: def.component });
      kit.remember({ tarp: head.tarpId, tarpLevel: head.level });
      const ladder = (def?.levels ?? []).map((l) => `${l.level === head.level ? `**${l.level}**` : l.level} (${l.owner})`).join(' → ');
      const others = list.slice(1).map((x) => `${x.tarpId} ${x.level}`).join(', ');
      const markdown =
        `**${machine}${subjectNote(subj)} · ${head.tarpId}** — **${head.level}**${head.since ? ` since ${stamp(head.since)}` : ''} ` +
        `(${BASIS_LABEL[head.basis]}${head.latest !== null ? `, latest ${head.latest}${def?.unit ? ` ${def.unit}` : ''}` : ''}).\n\n` +
        `${def ? `${def.title}. ` : ''}Trigger: ${head.trigger}\n\n` +
        `Actions (owner **${head.owner}**):\n${head.actions.map((a) => `- ${a}`).join('\n')}\n\n` +
        `Ladder: ${ladder}.${others ? ` Other TARPs on ${machine}: ${others}.` : ''}\n\n` +
        `The 240 °C and 85 °C limits are the demo's; the other thresholds, windows and actions are proof-of-concept values for the site to replace with its own.`;
      return kit.done(markdown, ['TARPs', head.basis === 'live' ? 'telemetry-live' : head.basis === 'daily' ? 'daily telemetry' : 'events_daily'], {
        navigate: machineTab(machine, 'reliability', 'tarp'),
      });
    }

    if (named.length && !fleetAsk) {
      // The TARP itself, and who is on it now.
      const t = named[0];
      const on = fleetTarpStatuses(ctx).filter((x) => x.tarpId === t.id);
      kit.ran('tarp', { id: t.id });
      kit.ran('fleet_tarp_statuses', { tarp: t.id, above_normal: on.length });
      if (on[0]) kit.focus({ machine: on[0].machine, mine: BY_ID[on[0].machine].mine, component: t.component });
      const markdown =
        `**${t.id} — ${t.title}** (${t.unit}${t.limit !== null ? `, limit ${t.limit} ${t.unit}` : ''}; ${CLSNAME[t.class].toLowerCase()}s${t.alsoClasses?.length ? ` and ${t.alsoClasses.map((c) => CLSNAME[c].toLowerCase()).join(', ')}s` : ''}).\n\n` +
        t.levels
          .map((l) => `- **${l.level}** (${l.owner}) — ${l.trigger} ${l.actions.length ? `→ ${l.actions.join('; ')}.` : ''}`.trim())
          .join('\n') +
        `\n\n${on.length ? `Above Normal now: ${on.map((x) => `**${x.machine}** ${x.level}${x.since ? ` since ${stamp(x.since)}` : ''}`).join(', ')}.` : 'Every machine it covers is at Normal.'}` +
        `${named.length > 1 ? ` Also covering it: ${named.slice(1).map((x) => x.id).join(', ')}.` : ''}`;
      return kit.done(markdown, ['TARPs', 'telemetry-live', 'daily telemetry'], on[0] ? { navigate: machineTab(on[0].machine, 'reliability', 'tarp') } : undefined);
    }

    // Fleet: everything above Normal.
    const s = scopeOf(E);
    const rows = fleetTarpStatuses(ctx).filter((x) => inScope(x.machine, s));
    kit.ran('fleet_tarp_statuses', { above_normal: rows.length });
    if (rows[0]) kit.focus({ machine: rows[0].machine, mine: BY_ID[rows[0].machine].mine });
    const count = (lvl: TarpStatus['level']) => rows.filter((x) => x.level === lvl).length;
    const markdown =
      `**${rows.length} TARP${rows.length === 1 ? '' : 's'} above Normal${s.label}** — ${count('Level 3')} at Level 3, ${count('Level 2')} at Level 2, ${count('Level 1')} at Level 1.\n\n` +
      (rows.length
        ? `| Machine | TARP | Level | Since | Owner |\n|---|---|---|---|---|\n` +
          rows
            .slice(0, 10)
            .map((x) => `| ${x.machine} | ${x.tarpId} | ${x.level} | ${x.since ? stamp(x.since) : '—'} | ${x.owner} |`)
            .join('\n') +
          `${rows.length > 10 ? `\n\n…and ${rows.length - 10} more.` : ''}\n\n`
        : '') +
      `Levels are judged on the live feed where a machine has one, else the shift-end daily mean, else alarm-event counts. Ask about one machine for its actions and ladder.`;
    return kit.done(markdown, ['TARPs', 'telemetry-live', 'daily telemetry', 'events_daily']);
  },
};

// ---------------------------------------------------------------------------
// fmea_top — highest-RPN failure modes
// ---------------------------------------------------------------------------

export const fmeaTop: IntentDef = {
  id: 'fmea_top',
  family: 'operations',
  priority: 91,
  examples: ['What are the highest-RPN failure modes for trucks?', 'FMEA for T-09'],
  score: cues({
    any: [/\bfmea\b|\brpn\b|failure modes?|risk priority number/],
    base: 7,
  }),
  async answer(actx) {
    const { kit, E } = actx;
    const ctx = await reliability(kit);
    const subj: Subject = E.cls || E.mine ? {} : subjectOf(actx, { componentAlert: false });
    const machine = subj.machine;
    const cls = machine ? [BY_ID[machine].cls] : E.cls?.classes;
    const ref = E.components[0];
    const scored = fmeaRows(ctx);
    kit.ran('fmea_rows', { ...(machine ? { machine_id: machine } : {}), ...(cls ? { cls: cls.join(',') } : {}) });
    const rows = scored
      .filter((r) => !cls || cls.includes(r.class))
      .filter((r) => !ref || ref.component.test(r.component) || ref.text.test(r.component) || ref.text.test(r.failureMode));
    const top = rows.slice(0, 5);
    const scope = machine ? `${machine}${subjectNote(subj)} (${CLSNAME[BY_ID[machine].cls].toLowerCase()} FMEA)` : E.cls ? E.cls.label : 'the fleet';
    if (!top.length) {
      return kit.done(`**No FMEA rows for ${scope}**${ref ? ` on ${ref.word}` : ''} yet.`, ['FMEA']);
    }
    const reg = machine ? HEALTH.filter((h) => h.machine_id === machine) : [];
    const covering = machine && subj.alert ? fmeaFor(machine, subj.alert.comp) : null;
    const table =
      `| FMEA | Component · failure mode | S×O×D | RPN |\n|---|---|---|---|\n` +
      top
        .map((r) => {
          const score = reg.find((h) => h.component === r.component)?.health_score;
          return `| ${r.id} | ${r.component}${score !== undefined ? ` (${score}/100)` : ''} · ${r.failureMode} | ${r.severity}×${r.occurrence}×${r.detection} | **${r.rpn}** |`;
        })
        .join('\n');
    const first = top[0];
    if (machine) kit.focus({ machine, mine: BY_ID[machine].mine, component: first.component });
    else kit.focus({ component: first.component });
    const markdown =
      `**Highest-RPN failure modes · ${scope}**\n\n${table}\n\n` +
      `Top: **${first.id}** — ${first.effect}; cause ${first.cause.toLowerCase().replace(/\.$/, '')}. ` +
      `Recommended: ${first.recommendedAction} (owner ${first.owner}).` +
      `${covering && covering !== first.id ? ` ${subj.alert?.id}'s component is covered by ${covering}.` : ''}\n\n` +
      `O comes from breakdown and corrective work orders per machine-year where there are any; D is credited −2 for a TARP and −1 for an SPC-able metric. Severity and the failure modes are proof-of-concept values for the reliability engineer to confirm.`;
    return kit.done(markdown, ['FMEA', 'work_orders.csv', 'TARPs'], machine ? { navigate: machineTab(machine, 'reliability', 'fmea') } : undefined);
  },
};

// ---------------------------------------------------------------------------
// control_plan — what is measured and the reaction
// ---------------------------------------------------------------------------

export const controlPlan: IntentDef = {
  id: 'control_plan',
  family: 'operations',
  priority: 90,
  examples: ["What's the control plan for brake temperature?", 'Control plan for T-09'],
  score: cues({
    any: [/control plans?|reaction plans?|controlled characteristics?/],
    base: 7,
  }),
  async answer(actx) {
    const { kit, E, L } = actx;
    const subj: Subject = E.cls || E.mine ? {} : subjectOf(actx);
    const machine = subj.machine;
    const cls = machine ? [BY_ID[machine].cls] : (E.cls?.classes ?? classFromText(L)?.classes);
    const refs = E.components;
    kit.ran('control_plans', { ...(machine ? { machine_id: machine } : {}), ...(refs[0] ? { component: refs[0].word } : {}) });
    let rows = CONTROL_PLANS.filter((c) => !cls || cls.includes(c.class)).filter(
      (c) => !refs.length || refs.some((ref) => ref.component.test(c.component) || ref.text.test(c.component) || ref.text.test(c.characteristic)),
    );
    const temp = /temperature|\btemp\b/.test(L);
    if (temp && rows.some((c) => /temperature/i.test(c.characteristic))) {
      rows = [...rows.filter((c) => /temperature/i.test(c.characteristic)), ...rows.filter((c) => !/temperature/i.test(c.characteristic))];
    }
    let levels = new Map<string, TarpStatus['level']>();
    if (machine) {
      const ctx = await reliability(kit);
      levels = new Map(tarpStatuses(machine, ctx).map((s) => [s.tarpId, s.level]));
      kit.focus({ machine, mine: BY_ID[machine].mine, ...(subj.alert ? { alert: subj.alert.id } : {}) });
    }
    if (!rows.length) {
      return kit.done(`**No control-plan rows${machine ? ` for ${machine}` : ''}${refs[0] ? ` on ${refs[0].word}` : ''}** yet.`, ['control plans']);
    }
    kit.focus({ component: rows[0].component });
    const show = rows.slice(0, 4);
    const body = show
      .map(
        (c) =>
          `- **${c.characteristic}** (${CLSNAME[c.class].toLowerCase()} · ${c.component}) — ${c.method}; spec ${c.specLimit}; ${c.frequency}. ` +
          `Reaction: ${c.reactionPlan}${c.tarpId ? ` [${c.tarpId}${levels.get(c.tarpId) ? ` — ${machine} at ${levels.get(c.tarpId)}` : ''}]` : ''}.`,
      )
      .join('\n');
    const markdown =
      `**Control plan${machine ? ` · ${machine}${subjectNote(subj)}` : ''}${refs[0] ? ` · ${refs[0].word}` : ''}** — ${rows.length} controlled characteristic${rows.length === 1 ? '' : 's'}.\n\n${body}` +
      `${rows.length > show.length ? `\n\n…and ${rows.length - show.length} more.` : ''}\n\n` +
      `Each row that has a TARP escalates through it. The brake and hydraulic limits are the demo's; the rest are plausible site values for the customer to replace with their own control plan.`;
    return kit.done(markdown, ['control plans', 'TARPs'], machine ? { navigate: machineTab(machine, 'reliability', 'control') } : undefined);
  },
};
